import { AxiosError } from 'axios';
import { ApiError, ApiErrorCode, ApiErrorDetails } from './api-error';

interface ErrorResponse {
  message?: string;
  error?: string;
  errors?: ApiErrorDetails[];
  statusCode?: number;
}

export class ErrorHandler {
  /**
   * Handle Axios errors and convert to ApiError
   */
  static handleAxiosError(error: AxiosError<ErrorResponse>): ApiError {
    // Network error (no response received)
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return new ApiError(
          'Request timed out',
          ApiErrorCode.TIMEOUT,
          undefined,
          { originalMessage: error.message }
        );
      }

      return new ApiError(
        'Network error occurred',
        ApiErrorCode.NETWORK_ERROR,
        undefined,
        { originalMessage: error.message }
      );
    }

    const { status, data } = error.response;
    const message = data?.message || data?.error || error.message;

    if (process.env.NODE_ENV === 'development') {
      console.log(`[API ${status}] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        requestBody: error.config?.data ? JSON.parse(error.config.data) : undefined,
        responseBody: data,
      });
    }

    // Map HTTP status codes to ApiErrorCode
    switch (status) {
      case 400:
        return new ApiError(
          message,
          ApiErrorCode.VALIDATION_ERROR,
          status,
          { responseBody: data, validationErrors: (data as any)?.errors }
        );

      case 401:
        return new ApiError(
          message || 'Authentication required',
          ApiErrorCode.UNAUTHORIZED,
          status
        );

      case 403:
        return new ApiError(
          message || 'Access forbidden',
          ApiErrorCode.FORBIDDEN,
          status
        );

      case 404:
        return new ApiError(
          message || 'Resource not found',
          ApiErrorCode.NOT_FOUND,
          status
        );

      case 422:
        return new ApiError(
          message || 'Validation failed',
          ApiErrorCode.VALIDATION_ERROR,
          status,
          data?.errors ? { validationErrors: data.errors } : undefined
        );

      case 500:
      case 502:
      case 503:
      case 504:
        return new ApiError(
          'Server error occurred',
          ApiErrorCode.SERVER_ERROR,
          status,
          { originalMessage: message }
        );

      default:
        return new ApiError(
          message || 'An error occurred',
          ApiErrorCode.UNKNOWN_ERROR,
          status
        );
    }
  }

  /**
   * Handle generic errors
   */
  static handleError(error: AxiosError<ErrorResponse> | Error): ApiError {
    // Already an ApiError
    if (ApiError.isApiError(error)) {
      return error;
    }

    // Axios error
    if (this.isAxiosError(error)) {
      return this.handleAxiosError(error as AxiosError<ErrorResponse>);
    }

    // Standard Error
    if (error instanceof Error) {
      return new ApiError(
        error.message,
        ApiErrorCode.UNKNOWN_ERROR,
        undefined,
        { originalError: error.name }
      );
    }

    // Unknown error type
    return new ApiError(
      'An unknown error occurred',
      ApiErrorCode.UNKNOWN_ERROR,
      undefined,
      { error: String(error) }
    );
  }

  /**
   * Type guard for AxiosError
   */
  private static isAxiosError(error: unknown): error is AxiosError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'isAxiosError' in error &&
      (error as AxiosError).isAxiosError === true
    );
  }

  /**
   * Log error to console (in development) or monitoring service (in production)
   */
  static logError(error: ApiError, context?: string): void {
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (isDevelopment) {
      console.group(`🔴 API Error ${context ? `(${context})` : ''}`);
      console.error('Message:', error.message);
      console.error('Code:', error.code);
      console.error('Status:', error.statusCode);
      console.error('Details:', error.details);
      console.error('Timestamp:', error.timestamp);
      console.error('Stack:', error.stack);
      console.groupEnd();
    } else {
      // In production, send to monitoring service (Sentry, DataDog, etc.)
      // Example: Sentry.captureException(error);
      console.error('API Error:', error.toJSON());
    }
  }
}